import React from 'react'
import { usePage } from '@inertiajs/react'
import { Menu, Building2 } from 'lucide-react'
import JumpToProperty from './JumpToProperty'
import AccountSwitcher from './AccountSwitcher'
import PipelineStatusStrip from './DataFreshnessIndicator'
import PushStatusBadge from './PushStatusBadge'
// import NotificationBell from './NotificationBell' // TEMP (hidden 2026-06-19): notifications bell hidden in both headers — re-enable with the <NotificationBell> below
import UserMenu from './UserMenu'
import { Link } from '@inertiajs/react'
import { Crown, Layers } from 'lucide-react'
import type { ReportingScope, SyncStatus, AccountPropertyGroup, CurrentAccount } from '@/types/shared'

interface TopBarProps {
  currentUser: { id: number; email: string }
  // `basePath` is backend-served; the header appends fixed suffixes to it and never rebuilds a URL.
  property: { id: number; basePath: string; name: string; currency: string; timezone: string }
  properties: AccountPropertyGroup[]
  currentAccount: CurrentAccount | null
  onToggleSidebar: () => void
  syncStatus?: SyncStatus | null
}

export default function TopBar({ currentUser, property, properties, currentAccount, onToggleSidebar, syncStatus }: TopBarProps) {
  const { canManageBilling, reportingScope } = usePage<{
    canManageBilling?: boolean
    reportingScope?: ReportingScope | null
  }>().props

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-border/60 bg-white/80 backdrop-blur-md px-4 sm:px-6">
      <button
        onClick={onToggleSidebar}
        className="inline-flex items-center justify-center rounded-lg p-2 text-muted-foreground cursor-pointer hover:bg-muted hover:text-foreground transition-colors lg:hidden"
        aria-label="Toggle sidebar"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Account context — only when the user spans more than one account (tenancy-14) */}
      <AccountSwitcher properties={properties} currentAccount={currentAccount} mode="property" />

      {/* Breadcrumb-style context */}
      {/* One line, always: a long hotel name truncates (its full name on hover) rather than wrapping the
          bar, and the chips never wrap. */}
      <div className="hidden min-w-0 items-center gap-2 text-sm lg:flex">
        <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="truncate font-medium text-foreground" title={property.name}>{property.name}</span>
        {/* The combined inventory, in the breadcrumb where the operator reads which listing he is on.
            The sidebar strip explains it; this is the one-glance version that stays put as he moves
            around, because "why did my pricing page just send me somewhere else?" is a header-level
            question. Absent for any other property: the breadcrumb is then unchanged. The combined
            inventory is called by its lead listing's name, so the lead's chip names no one (the
            breadcrumb already does) and a following listing's chip links to the lead, named on hover, so the
            chip stays two words long whatever the lead is called. */}
        {reportingScope && (
          <>
            <span className="shrink-0 text-muted-foreground/50">·</span>
            {reportingScope.isLead ? (
              <span
                className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary"
                title="Prices set here cover every listing in this combined inventory"
              >
                <Crown className="h-2.5 w-2.5" />
                Lead listing
              </span>
            ) : (
              reportingScope.leadBasePath ? (
                <Link
                  href={reportingScope.leadBasePath}
                  className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700 transition-colors hover:bg-slate-200"
                  title={`Priced at ${reportingScope.leadPropertyName}, the lead listing`}
                >
                  <Layers className="h-2.5 w-2.5" />
                  Following listing
                </Link>
              ) : (
                // A lead the person cannot open (change safety R4.3): no name and no link to it.
                <span
                  className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700"
                  title="Priced at the lead listing"
                >
                  <Layers className="h-2.5 w-2.5" />
                  Following listing
                </span>
              )
            )}
          </>
        )}
        <span className="shrink-0 text-muted-foreground/50">·</span>
        <span className="shrink-0 text-muted-foreground">{property.currency}</span>
      </div>

      <div className="ml-auto flex shrink-0 items-center gap-3">
        <PipelineStatusStrip syncStatus={syncStatus ?? null} />
        {/* Two-state live/paused indicator + its popover (pause lives here, not in a
            standing dashboard banner). Sits in the always-visible action group rather
            than the lg-only breadcrumb so the paused state is honest on mobile too. */}
        <PushStatusBadge property={{ basePath: property.basePath, name: property.name }} syncStatus={syncStatus} />
        {/* Shared property jump (⌘K, search, keyboard nav) — scoped to the current business.
            Cross-business movement is handled by the AccountSwitcher above. */}
        <JumpToProperty properties={properties} currentAccount={currentAccount} mode="property" currentProperty={{ id: property.id, name: property.name }} />

        {/* TEMP (hidden 2026-06-19): notifications bell hidden until we revisit it.
            When restoring, wire it to the property's sync/target warnings (syncStatus + targetAlerts),
            and uncomment the NotificationBell import above.
        <NotificationBell /> */}

        {/* Shared avatar + account menu — identical items to the portfolio header
            (billing, settings, help, log out). Help & guides are global. */}
        <UserMenu
          currentUser={currentUser}
          canManageBilling={canManageBilling}
          helpHref="/guides"
          settingsHref={currentAccount?.settingsPath ?? null}
        />
      </div>
    </header>
  )
}
